/**
 * Normalização de texto para busca e comparação.
 *
 * Os nomes da base vêm de origens diferentes (planilha, digitação,
 * importação), então "ANDRÉA", "Andrea" e "Andréa  Maria" precisam ser
 * tratados como o mesmo texto — senão quem procura "Andrea" não encontra
 * "ANDRÉA" e acaba cadastrando a pessoa de novo.
 */

/** Minúsculas, sem acento e sem espaço duplicado. */
export const normalizeText = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

/** true se `haystack` contém `needle`, ignorando acento e caixa. */
export const includesNormalized = (haystack, needle) => {
  const alvo = normalizeText(needle);
  if (!alvo) return true;
  return normalizeText(haystack).includes(alvo);
};

/**
 * true se cada palavra de `needle` aparece em `haystack`, em qualquer ordem,
 * ignorando acento e caixa. Serve para buscar nome completo: "rita de
 * cascia" e "rita coutinho" encontram "RITA DE CÁSCIA ALVES COUTINHO" mesmo
 * com o nome guardado em dois campos (first_name / last_name).
 */
export const includesAllWords = (haystack, needle) => {
  const palavras = normalizeText(needle).split(' ').filter(Boolean);
  if (palavras.length === 0) return true;
  const alvo = normalizeText(haystack);
  return palavras.every((palavra) => alvo.includes(palavra));
};

// Abreviações usuais em nomes de servidores da rede ("Mª Cláudia").
const ABREVIACOES = {
  m: 'maria',
  ma: 'maria',
  fco: 'francisco',
  fca: 'francisca',
  ant: 'antonio',
};

const PARTICULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);

/**
 * Chave de comparação de nome de pessoa: além de tirar acento e caixa,
 * expande abreviações e descarta partículas, para que "Mª Cláudia Oliveira"
 * e "MARIA CLAUDIA DE OLIVEIRA" caiam na mesma chave.
 * Espelha `normalize_person_name` do backend (api/text_utils.py).
 */
export const normalizePersonName = (value) =>
  normalizeText(value)
    .replace(/[.ºª]/g, ' ')
    .split(' ')
    .filter((parte) => parte && !PARTICULAS.has(parte))
    .map((parte) => ABREVIACOES[parte] || parte)
    .join(' ');

export default normalizeText;
